import React, { useEffect, useMemo, useState } from 'react';
import { FaPrint, FaTimes } from 'react-icons/fa';
import * as orderService from '../../services/order.service';
import * as clientCatalogService from '../../services/clientCatalog.service';
import { mainImageSrc } from '../TearSheet/tearSheetUtils';
import './OrderProductionSummary.css';

/** dd/MM/yyyy from an ISO or yyyy-mm-dd string. */
const fmtDMY = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const num = (v) => {
  const n = parseFloat(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

/** Join a finish/material value that may be an array of strings/objects, an object, or a string. */
const joinNames = (v) => {
  if (!v) return '';
  if (Array.isArray(v)) {
    return v.map((x) => (typeof x === 'string' ? x : x?.name)).filter(Boolean).join(', ');
  }
  if (typeof v === 'object') return v.name || '';
  return String(v);
};

/** "33.00x23.00x19.00 CM" from a pack row's dims + unit. */
const packDims = (pack, unit) => {
  if (!pack) return '';
  const parts = [pack.length, pack.breadth, pack.height].map((x) => {
    const n = num(x);
    return n === null ? null : n.toFixed(2);
  });
  if (parts.some((p) => p === null)) return '';
  return `${parts[0]}x${parts[1]}x${parts[2]} ${String(unit || '').toUpperCase()}`.trim();
};

const packQty = (pack) => (pack?.quantityPack ?? pack?.quantity_pack ?? 0) || 0;
const packWt = (pack) => {
  const n = num(pack?.grossWt ?? pack?.netWt ?? pack?.net_wt);
  return n === null ? '0.0' : n.toFixed(1);
};

/** Product-level barcode { type, value } with defensive field fallbacks. */
const productBarcode = (cat, orderItem) => ({
  type: cat?.barcodeType?.name || orderItem?.barcode || '',
  value: cat?.barcode || cat?.barcodeValue || cat?.ean || cat?.barcodeNumber || '',
});

/** Vertical (rotated) text wrapper — wraps to multiple lines within a capped height. */
const V = ({ children, className = '' }) => (
  <span className={`ps-vtext ${className}`.trim()}>{children}</span>
);

/** Barcode as up to two stacked lines: type over value. */
const barcodeLines = (bc) => {
  if (!bc || (!bc.type && !bc.value)) return '—';
  return (
    <>
      {bc.type}
      {bc.value ? (
        <>
          <br />
          {bc.value}
        </>
      ) : null}
    </>
  );
};

const OrderProductionSummary = ({ order, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    if (!order?.id) return undefined;
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const full = await orderService.getOrder(order.id);
        if (!active) return;
        setDetail(full);
        const clientId = full?.clientId;
        if (clientId) {
          const cat = await clientCatalogService.getClientCatalog(clientId);
          if (active) setCatalog(Array.isArray(cat) ? cat : []);
        }
      } catch (e) {
        console.error('Failed to load production summary', e);
        if (active) setError('Failed to load the production summary for this order.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [order?.id]);

  // Merge each order line with its catalog line (matched by product uid) for the rich columns.
  const rows = useMemo(() => {
    const items = detail?.items || [];
    const byUid = new Map(catalog.map((c) => [c.uid, c]));
    return items.map((it) => {
      const cat = byUid.get(it.productUid) || {};
      const unit = cat.dimensionUnit || '';
      const inner = (Array.isArray(cat.innerPacks) ? cat.innerPacks : [])[0] || null;
      const master = (Array.isArray(cat.masterPacks) ? cat.masterPacks : [])[0] || null;
      const weight = num(cat.netWeight ?? cat.netWt ?? cat.weight ?? cat.grossWt);
      return {
        image: mainImageSrc(cat),
        itemNo: cat.itemNo || it.pCode || '—',
        productName: cat.productName || it.description || '—',
        customerArticle: it.clientSku || cat.skuId || '—',
        dimm: it.dimension || '',
        weight: weight === null ? '' : weight.toFixed(3),
        barcode: productBarcode(cat, it),
        finish: joinNames(cat.finish) || joinNames(cat.material) || joinNames(cat.materials) || '—',
        cartons: it.cartons ? Math.round(it.cartons) : 0,
        orderQty: it.orderQty ? Math.round(it.orderQty) : 0,
        inner: {
          barcode: inner ? productBarcode(cat, it) : { type: '', value: '' },
          qty: inner ? packQty(inner) : 0,
          dims: packDims(inner, unit),
          weight: inner ? packWt(inner) : '0.0',
        },
        master: {
          barcode: master ? productBarcode(cat, it) : { type: '', value: '' },
          qty: master ? packQty(master) : 0,
          dims: packDims(master, unit),
          weight: master ? packWt(master) : '0.0',
        },
      };
    });
  }, [detail, catalog]);

  // Header fields (fall back to the passed-in list row while the detail loads).
  const header = {
    authDt: fmtDMY(detail?.authorizedAt ?? order?.authorizedAt),
    authBy: detail?.merchant ?? order?.merchant ?? '',
    updateDt: fmtDMY(detail?.updatedAt),
    version: 1,
    totalArticles: rows.length,
    shipDate: fmtDMY(detail?.shippingDate),
    inspectionDate: '', // not tracked on the order
    customerPo: detail?.buyerPo ?? order?.buyerPo ?? '',
    orderNo: detail?.orderNo ?? order?.orderNo ?? '',
  };

  const leftBand = [
    { label: 'TOTAL NO OF ARTICLES', value: header.totalArticles },
    { label: 'SHIP DATE', value: header.shipDate || '—' },
    { label: 'INSPECTION DATE', value: header.inspectionDate || '—' },
    { label: 'CUSTOMER PO #', value: header.customerPo || '—' },
    { label: 'ORDER NO.', value: header.orderNo || '—' },
  ];

  return (
    <div className="ps-overlay" role="dialog" aria-modal="true">
      <div className="ps-shell">
        <div className="ps-toolbar">
          <div className="ps-toolbar-title">
            Production Summary — <strong>{order?.orderNo}</strong>
          </div>
          <div className="ps-toolbar-actions">
            <button type="button" className="btn btn-primary btn-sm" onClick={() => window.print()} disabled={loading || !!error}>
              <FaPrint className="me-1" /> Print / Save PDF
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>
              <FaTimes className="me-1" /> Close
            </button>
          </div>
        </div>

        <div className="ps-scroll">
          {loading ? (
            <div className="ps-status">Loading production summary…</div>
          ) : error ? (
            <div className="ps-status ps-status-error">{error}</div>
          ) : (
            <div className="ps-doc" id="production-summary-doc">
              {/* Top bar: Auth Dt / Auth By / title / Update Dt / Version */}
              <div className="ps-topbar">
                <span className="ps-top-item">Auth. Dt. <b>{header.authDt || ''}</b></span>
                <span className="ps-top-item">Auth. By : <b>{header.authBy || ''}</b></span>
                <h2 className="ps-top-title">Production Summary</h2>
                <span className="ps-top-item">Update Dt. <b>{header.updateDt || ''}</b></span>
                <span className="ps-top-item">Version : <b>{header.version}</b></span>
              </div>

              <div className="ps-body">
                {/* Left vertical info band */}
                <div className="ps-leftband">
                  {leftBand.map((cell) => (
                    <div className="ps-lb-cell" key={cell.label}>
                      <span className="ps-lb-label">{cell.label}</span>
                      <span className="ps-lb-value">{cell.value}</span>
                    </div>
                  ))}
                </div>

                {/* Main product grid */}
                <table className="ps-table">
                  <thead>
                    <tr>
                      <th rowSpan={2} className="ps-col-sno"><V>S.NO.</V></th>
                      <th rowSpan={2} className="ps-col-img">IMAGE</th>
                      <th rowSpan={2}><V>ITEM NO.</V></th>
                      <th rowSpan={2}><V>Product Name</V></th>
                      <th rowSpan={2}><V>CUSTOMER ARTICLE</V></th>
                      <th colSpan={3} className="ps-group">PRODUCT</th>
                      <th rowSpan={2}><V>FINISH</V></th>
                      <th rowSpan={2}><V>NO OF CARTON</V></th>
                      <th rowSpan={2}><V>ORDER QTY</V></th>
                      <th colSpan={4} className="ps-group">INNER BOX</th>
                      <th colSpan={4} className="ps-group">MASTER BOX</th>
                      <th rowSpan={2} className="ps-col-comments">COMMENTS</th>
                    </tr>
                    <tr>
                      <th><V>DIMM</V></th>
                      <th><V>WEIGHT (IN KGS)</V></th>
                      <th><V>BAR CODE</V></th>
                      <th><V>BAR CODE</V></th>
                      <th><V>QTY</V></th>
                      <th><V>DIMM</V></th>
                      <th><V>WEIGHT (IN KGS)</V></th>
                      <th><V>BAR CODE</V></th>
                      <th><V>QTY</V></th>
                      <th><V>DIMM</V></th>
                      <th><V>WEIGHT (IN KGS)</V></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={20} className="ps-empty">This order has no line items.</td>
                      </tr>
                    ) : (
                      rows.map((r, i) => (
                        <tr key={i} className="row-summary">
                          <td className="ps-col-sno">{i + 1}</td>
                          <td className="ps-img-cell">
                            {r.image ? <img src={r.image} alt={r.itemNo} className="ps-img" /> : '—'}
                          </td>
                          <td><V>{r.itemNo}</V></td>
                          <td><V>{r.productName}</V></td>
                          <td><V>{r.customerArticle}</V></td>
                          <td><V>{r.dimm || '—'}</V></td>
                          <td><V>{r.weight || '—'}</V></td>
                          <td><V>{barcodeLines(r.barcode)}</V></td>
                          <td><V>{r.finish}</V></td>
                          <td><V>{r.cartons}</V></td>
                          <td><V>{r.orderQty}</V></td>
                          <td><V>{barcodeLines(r.inner.barcode)}</V></td>
                          <td><V>{r.inner.qty}</V></td>
                          <td><V>{r.inner.dims || '0'}</V></td>
                          <td><V>{r.inner.weight}</V></td>
                          <td><V>{barcodeLines(r.master.barcode)}</V></td>
                          <td><V>{r.master.qty}</V></td>
                          <td><V>{r.master.dims || '0'}</V></td>
                          <td><V>{r.master.weight}</V></td>
                          <td className="ps-col-comments" />
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderProductionSummary;
